import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import AgentDashboardPage from "@/pages/AgentDashboardPage";
import IncentiveApprovalsPage from "@/pages/IncentiveApprovalsPage";
import WarrantyApprovalsPage from "@/pages/WarrantyApprovalsPage";
import UniqueIdsPage from "@/pages/UniqueIdsPage";
import PayoutsPage from "@/pages/PayoutsPage";
import RecordsListPage from "@/pages/RecordsListPage";
import RecordDetailPage from "@/pages/RecordDetailPage";
import NewRecordPage from "@/pages/NewRecordPage";
import UsersPage from "@/pages/UsersPage";
import TerritoriesPage from "@/pages/TerritoriesPage";
import StoresPage from "@/pages/StoresPage";
import IncentivesPage from "@/pages/IncentivesPage";
import AuditPage from "@/pages/AuditPage";
import SettingsPage from "@/pages/SettingsPage";
import HandoffPage from "@/pages/HandoffPage";
import AppShell from "@/components/AppShell";

function Protected({ children, adminOnly = false, blockAgent = false, roles = null }) {
  const { user } = useAuth();
  if (user === null) {
    return <div className="min-h-screen grid place-items-center text-[#71717A]">Checking session…</div>;
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (blockAgent && user.role === "sales_agent") return <Navigate to="/dashboard" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <AppShell>{children}</AppShell>;
}

function DashboardRouter() {
  const { user } = useAuth();
  if (user?.role === "sales_agent") return <AgentDashboardPage />;
  if (user?.role === "sales_incentive") return <IncentiveApprovalsPage />;
  return <DashboardPage />;
}

function LoginGate() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-[#71717A]">Checking session…</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LoginPage />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginGate />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Protected><DashboardRouter /></Protected>} />
            <Route path="/records" element={<Protected blockAgent><RecordsListPage /></Protected>} />
            <Route path="/records/:id" element={<Protected blockAgent><RecordDetailPage /></Protected>} />
            <Route path="/new" element={<Protected blockAgent><NewRecordPage /></Protected>} />
            <Route path="/handoff" element={<Protected blockAgent><HandoffPage /></Protected>} />
            <Route path="/users" element={<Protected adminOnly><UsersPage /></Protected>} />
            <Route path="/territories" element={<Protected adminOnly><TerritoriesPage /></Protected>} />
            <Route path="/stores" element={<Protected adminOnly><StoresPage /></Protected>} />
            <Route path="/incentives" element={<Protected adminOnly><IncentivesPage /></Protected>} />
            <Route path="/unique-ids" element={<Protected roles={["admin", "sales_incentive"]}><UniqueIdsPage /></Protected>} />
            <Route path="/incentive-approvals" element={<Protected roles={["admin", "sales_incentive"]}><IncentiveApprovalsPage /></Protected>} />
            <Route path="/warranty-approvals" element={<Protected><WarrantyApprovalsPage /></Protected>} />
            <Route path="/payouts" element={<Protected roles={["admin", "sales_incentive"]}><PayoutsPage /></Protected>} />
            <Route path="/audit" element={<Protected adminOnly><AuditPage /></Protected>} />
            <Route path="/settings" element={<Protected adminOnly><SettingsPage /></Protected>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
